/**
 * Provider event reconnaissance (task 1).
 *
 * Two things about this provider are unknown, and both block the measurement
 * work: whether a translation session ever reports token usage at all — in the
 * Realtime API usage rides on `response.done`, and this session has no
 * `response.create` and no turn-taking, so that event may simply never come —
 * and whether a "part" has a boundary we could measure, which needs some kind
 * of `...done` / `...completed` event that we have never handled. Guessing
 * costs more than looking, so this looks.
 *
 * What it may look at is decided by section 9, not by convenience: speech must
 * never reach the server. `session.input_transcript.delta` and
 * `session.output_transcript.delta` carry the actual words, so this module is
 * built so that no value from an event can leave it by accident. The shape it
 * produces is the *shape* of the traffic, never its content:
 *
 *   - the event `type`, which is an enum;
 *   - how many of each arrived, when the first and last one did;
 *   - the *names* of the fields present, and how often each was present;
 *   - for string fields, the length only — never a character of the value;
 *   - for numeric fields, the value in full, because token counts and audio
 *     seconds are the entire point of the exercise;
 *   - for a short list of plainly-enum fields (`status`, `model`, …), the
 *     value, and only when it also looks like an enum rather than a sentence.
 *
 * The rejected alternative was the obvious one: log each event as it arrives
 * and read the console. It fails twice over — a delta stream is hundreds of
 * events a minute, so the interesting event would be buried under transcript
 * chatter, and the only way to get the result off the tester's machine and
 * into the server log would have been to ship raw events, which is exactly the
 * thing section 9 forbids. So the browser aggregates and reports a summary:
 * one telemetry event per *distinct event type*, a handful per call, instead
 * of one HTTP request per provider event.
 */

/** One telemetry record, in the shape `Telemetry.record` takes. */
export interface ReconReport {
  name: string;
  values: Record<string, number>;
  tags: Record<string, string>;
}

/**
 * The key prefixes a reader of the log needs, because a flattened path is
 * meaningless without them. They also keep recon keys from colliding with the
 * fields the telemetry route logs itself (`roomId`, `seat`, `sessionId`).
 *
 *   f.<path>  in how many events this field was present
 *   n.<path>  the last numeric value seen at this path
 *   t.<path>  those numeric values added up, when more than one was seen
 *   l.<path>  total length of the string at this path, never its content
 *   m.<path>  the longest single string seen at this path
 *   e.<path>  (a tag) the last value, for the few fields that are enums
 */
const PRESENT = 'f.';
const LAST_NUMBER = 'n.';
const TOTAL_NUMBER = 't.';
const TOTAL_LENGTH = 'l.';
const MAX_LENGTH = 'm.';
const ENUM = 'e.';

/**
 * Fields whose value may be recorded verbatim. Everything not named here is
 * measured by length, on the assumption that it could be speech — including
 * fields we have never seen, which is the case that matters, since the whole
 * point of a recon run is to discover fields nobody wrote this list against.
 */
const ENUM_FIELDS = new Set([
  'type',
  'status',
  'object',
  'model',
  'role',
  'format',
  'modality',
  'voice',
  'reason',
  'code',
]);

/** An enum, a model id or a status — anything with a space is not one. */
const ENUM_VALUE_RE = /^[A-Za-z0-9_.:/-]{1,48}$/;

/** A field name we are willing to repeat. See `safeSegment`. */
const SAFE_SEGMENT_RE = /^[A-Za-z0-9_-]{1,40}$/;

/** Telemetry keys are capped at 48 characters by the shared schema. */
const MAX_KEY = 48;

/** How deep into a nested event to walk before calling it structure enough. */
const MAX_DEPTH = 6;

/**
 * Per event type. Beyond this the payload stops being a summary, and a type
 * with hundreds of distinct paths has told us what we needed anyway: that it
 * is large. The count of what was dropped is reported rather than hidden.
 */
const MAX_PATHS_PER_TYPE = 64;

interface NumberTally {
  last: number;
  sum: number;
  samples: number;
}

interface StringTally {
  totalLength: number;
  maxLength: number;
}

interface TypeTally {
  count: number;
  firstMs: number;
  lastMs: number;
  maxBytes: number;
  present: Map<string, number>;
  numbers: Map<string, NumberTally>;
  strings: Map<string, StringTally>;
  enums: Map<string, string>;
  dropped: number;
}

export interface ReconOptions {
  /**
   * Called with a complete, cumulative summary. Cumulative rather than
   * incremental on purpose: telemetry delivery is best-effort and silent when
   * it fails, so every report has to be readable on its own — the last line in
   * the log answers the question even if the three before it never arrived.
   */
  report(reports: ReconReport[]): void;
  /** How often, in session milliseconds, an interim summary is sent. */
  snapshotMs?: number;
}

export class ProviderEventRecon {
  #report: (reports: ReconReport[]) => void;
  #snapshotMs: number;
  #types = new Map<string, TypeTally>();
  #events = 0;
  #lastReportMs = 0;
  #snapshot = 0;
  #finished = false;

  constructor({ report, snapshotMs = 30_000 }: ReconOptions) {
    this.#report = report;
    this.#snapshotMs = snapshotMs;
  }

  /**
   * Fold one provider event into the tally.
   *
   * `atMs` is milliseconds since the session started connecting, so the
   * numbers in the log are readable without knowing the tester's clock, and
   * `bytes` is the length of the frame as it arrived — a cheap proxy for which
   * events are big, which the flattened paths alone do not show.
   *
   * This runs on the data-channel message handler, in front of the code that
   * paints subtitles, so it does bookkeeping and nothing else: no JSON is
   * re-parsed (the caller hands over the object it already parsed), nothing is
   * awaited, and no network call happens here — the interim report goes onto
   * the telemetry queue, which flushes on its own schedule.
   */
  observe(event: unknown, atMs: number, bytes: number): void {
    if (this.#finished) return;

    const type = enumOrNull((event as { type?: unknown })?.type) ?? 'unparsed';
    const tally = this.#tallyFor(type, atMs);
    tally.count += 1;
    tally.lastMs = atMs;
    tally.maxBytes = Math.max(tally.maxBytes, bytes);
    this.#events += 1;

    this.#walk(tally, event, '', 0);

    // The stream itself is the clock. A timer would have been a second thing
    // to cancel on every exit path in the adapter, and a stream that has gone
    // quiet has nothing new to report anyway.
    if (atMs - this.#lastReportMs >= this.#snapshotMs) this.#emit(atMs, false);
  }

  /** The last word, sent whether or not a snapshot is due. */
  finish(atMs: number): void {
    if (this.#finished) return;
    this.#emit(atMs, true);
    this.#finished = true;
  }

  #tallyFor(type: string, atMs: number): TypeTally {
    const existing = this.#types.get(type);
    if (existing) return existing;
    const fresh: TypeTally = {
      count: 0,
      firstMs: atMs,
      lastMs: atMs,
      maxBytes: 0,
      present: new Map(),
      numbers: new Map(),
      strings: new Map(),
      enums: new Map(),
      dropped: 0,
    };
    this.#types.set(type, fresh);
    return fresh;
  }

  /**
   * Flattens one event into paths. Array indices collapse to `[]` — an event
   * with ten content parts should teach us that there is a `content` list with
   * a `type` on each item, not produce ten near-identical keys — and the
   * length of the array is kept as a number instead.
   */
  #walk(tally: TypeTally, node: unknown, path: string, depth: number): void {
    if (node === null || node === undefined) return;

    if (Array.isArray(node)) {
      this.#number(tally, `${path}[#]`, node.length);
      if (depth >= MAX_DEPTH) return;
      for (const item of node) this.#walk(tally, item, `${path}[]`, depth + 1);
      return;
    }

    if (typeof node === 'object') {
      if (depth >= MAX_DEPTH) return;
      for (const [rawKey, value] of Object.entries(node as Record<string, unknown>)) {
        const key = safeSegment(rawKey);
        const child = path === '' ? key : `${path}.${key}`;
        this.#present(tally, child);
        this.#walk(tally, value, child, depth + 1);
      }
      return;
    }

    if (typeof node === 'number') {
      if (Number.isFinite(node)) this.#number(tally, path, node);
      return;
    }

    if (typeof node === 'boolean') {
      // Booleans are not speech and are frequently the answer: whether a
      // response was truncated, whether a session is still open.
      this.#number(tally, path, node ? 1 : 0);
      return;
    }

    if (typeof node === 'string') {
      const leaf = path.split('.').pop() ?? '';
      const value = enumOrNull(node);
      if (value !== null && ENUM_FIELDS.has(leaf.replace(/\[\]$/, ''))) {
        if (this.#room(tally, path)) tally.enums.set(path, value);
      }
      // Recorded for every string, enum or not: the length of the delta stream
      // is the only honest measure of how much text a part carried, and it is
      // the one thing about a transcript that is safe to send.
      this.#string(tally, path, node.length);
    }
  }

  #present(tally: TypeTally, path: string): void {
    if (!this.#room(tally, path)) return;
    tally.present.set(path, (tally.present.get(path) ?? 0) + 1);
  }

  #number(tally: TypeTally, path: string, value: number): void {
    if (!this.#room(tally, path)) return;
    const seen = tally.numbers.get(path);
    if (seen) {
      seen.last = value;
      seen.sum += value;
      seen.samples += 1;
    } else {
      tally.numbers.set(path, { last: value, sum: value, samples: 1 });
    }
  }

  #string(tally: TypeTally, path: string, length: number): void {
    if (!this.#room(tally, path)) return;
    const seen = tally.strings.get(path);
    if (seen) {
      seen.totalLength += length;
      seen.maxLength = Math.max(seen.maxLength, length);
    } else {
      tally.strings.set(path, { totalLength: length, maxLength: length });
    }
  }

  /** Whether this type may take on another path, counting the refusals. */
  #room(tally: TypeTally, path: string): boolean {
    const known =
      tally.present.has(path) ||
      tally.numbers.has(path) ||
      tally.strings.has(path) ||
      tally.enums.has(path);
    if (known) return true;
    const distinct = tally.present.size + tally.numbers.size + tally.strings.size;
    if (distinct < MAX_PATHS_PER_TYPE) return true;
    tally.dropped += 1;
    return false;
  }

  #emit(atMs: number, final: boolean): void {
    this.#lastReportMs = atMs;
    this.#snapshot += 1;
    const snapshot = this.#snapshot;

    let usageFields = 0;
    let boundaryEvents = 0;
    let dropped = 0;

    const reports: ReconReport[] = [];
    for (const [type, tally] of this.#types) {
      if (looksFinal(type)) boundaryEvents += tally.count;
      dropped += tally.dropped;

      const values: Record<string, number> = {
        snapshot,
        count: tally.count,
        firstMs: Math.round(tally.firstMs),
        lastMs: Math.round(tally.lastMs),
        maxBytes: tally.maxBytes,
      };
      if (tally.dropped > 0) values.pathsDropped = tally.dropped;

      for (const [path, times] of tally.present) values[capKey(PRESENT + path)] = times;
      for (const [path, n] of tally.numbers) {
        if (looksLikeUsage(path)) usageFields += 1;
        values[capKey(LAST_NUMBER + path)] = n.last;
        // A single sample's sum is its value, and a repeated one is worth
        // having: per-part token counts only add up to a bill if they add up.
        if (n.samples > 1) values[capKey(TOTAL_NUMBER + path)] = n.sum;
      }
      for (const [path, s] of tally.strings) {
        values[capKey(TOTAL_LENGTH + path)] = s.totalLength;
        values[capKey(MAX_LENGTH + path)] = s.maxLength;
      }

      const tags: Record<string, string> = { type };
      for (const [path, value] of tally.enums) tags[capKey(ENUM + path)] = value;

      reports.push({ name: 'provider-event', values, tags });
    }

    /**
     * The line that answers the two questions on its own, so a reader does not
     * have to add up the per-type ones: did anything numeric that smells of
     * usage arrive, and did anything claim a segment was complete.
     */
    reports.push({
      name: 'provider-recon',
      values: {
        snapshot,
        final: final ? 1 : 0,
        ms: Math.round(atMs),
        events: this.#events,
        types: this.#types.size,
        usageFields,
        boundaryEvents,
        pathsDropped: dropped,
      },
      tags: {},
    });

    this.#report(reports);
  }
}

/** `...done`, `...completed`, `...stopped`: a part with a real boundary. */
function looksFinal(type: string): boolean {
  return /\.(done|completed|stopped|finished)$/.test(type);
}

/** The fields task 3 is hunting for, named loosely because the names are not known. */
function looksLikeUsage(path: string): boolean {
  return /token|usage|billed|cost|duration|seconds|minutes|audio_ms/i.test(path);
}

/**
 * A field name, if it reads like one. The provider's schema decides these, so
 * in practice they are all `snake_case` — but an event could carry an object
 * keyed by something a person said, and a key is a value like any other. One
 * that does not look like an identifier is reduced to `?`, which still shows a
 * reader that *something* was nested there.
 */
function safeSegment(key: string): string {
  return SAFE_SEGMENT_RE.test(key) ? key : '?';
}

function enumOrNull(value: unknown): string | null {
  return typeof value === 'string' && ENUM_VALUE_RE.test(value) ? value : null;
}

/**
 * Telemetry keys are bounded at 48 characters. The tail is what distinguishes
 * `response.usage.input_token_details.audio_tokens` from its neighbour, so a
 * long path keeps its prefix letter and its end, and loses the middle.
 */
function capKey(key: string): string {
  if (key.length <= MAX_KEY) return key;
  return `${key.slice(0, 2)}~${key.slice(key.length - (MAX_KEY - 3))}`;
}
